import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export interface EmailAccount {
  id: string;
  user_id: string;
  provider: string;
  email_address: string;
  is_active: boolean;
  last_sync_at: string | null;
  created_at: string;
}

export function useEmailAccounts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: emailAccounts, isLoading } = useQuery({
    queryKey: ['email-accounts', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EmailAccount[];
    },
    enabled: !!user,
  });

  const connectGmail = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');

      // Generate cryptographically secure state token
      const stateToken = crypto.randomUUID() + '-' + Date.now();

      // Store state token in database with expiry
      const { error: stateError } = await supabase
        .from('oauth_state_tokens')
        .insert({
          state_token: stateToken,
          user_id: user.id,
        });

      if (stateError) {
        console.error('Failed to store state token:', stateError);
        throw new Error('Failed to initiate OAuth flow. Please try again.');
      }

      // Fetch the client ID from the edge function or config
      const { data: configData } = await supabase.functions.invoke('get-oauth-config');
      const clientId = configData?.client_id;

      if (!clientId) {
        throw new Error('OAuth configuration not found. Please contact support.');
      }

      const redirectUri = `https://ggugsgvsxjxurvvbdigc.supabase.co/functions/v1/gmail-oauth-callback`;
      const scope = 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile';
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: scope,
        access_type: 'offline',
        prompt: 'consent',
        state: stateToken,
      })}`;

      // Open in new window to avoid iframe restrictions
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        authUrl,
        'Gmail Authorization',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
      );
    },
    onSuccess: () => {
      toast({
        title: 'Authorization window opened',
        description: 'Please complete the Gmail authorization in the popup window.',
      });
    },
  });

  // Connect Microsoft Account
  const connectMicrosoft = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');

      // Generate state token
      const stateToken = crypto.randomUUID();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 minutes

      const { error } = await supabase
        .from('oauth_state_tokens')
        .insert({
          user_id: user.id,
          state_token: stateToken,
          expires_at: expiresAt,
        });

      if (error) throw error;

      // Get Microsoft client ID from env (will be fetched from edge function)
      const { data: configData } = await supabase.functions.invoke('get-oauth-config');
      const clientId = configData?.microsoft_client_id || '';
      
      const redirectUri = `https://ggugsgvsxjxurvvbdigc.supabase.co/functions/v1/microsoft-oauth-callback`;
      const scope = 'https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send offline_access openid profile email';
      
      const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
      authUrl.searchParams.set('client_id', clientId || '');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', stateToken);
      authUrl.searchParams.set('response_mode', 'query');
      authUrl.searchParams.set('prompt', 'select_account');

      // Open OAuth flow in new window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        authUrl.toString(),
        'Microsoft OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      return stateToken;
    },
    onSuccess: () => {
      toast({
        title: 'Connecting to Microsoft 365',
        description: 'Please authorize the application in the popup window.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to initiate Microsoft connection',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const syncAccount = useMutation({
    mutationFn: async (emailAddress: string) => {
      if (!user) throw new Error('User not authenticated');

      // Get account to determine provider
      const { data: account } = await supabase
        .from('email_accounts')
        .select('provider')
        .eq('user_id', user.id)
        .eq('email_address', emailAddress)
        .single();

      const provider = account?.provider || 'gmail';
      const functionName = provider === 'microsoft' ? 'microsoft-sync' : 'gmail-sync';

      const { error } = await supabase.functions.invoke(functionName, {
        body: { user_id: user.id, email_address: emailAddress },
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      toast({
        title: 'Sync started',
        description: 'Your emails are being synced in the background.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Sync failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const disconnectAccount = useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await supabase
        .from('email_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      toast({
        title: 'Account disconnected',
        description: 'Your email account has been removed.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    emailAccounts: emailAccounts || [],
    isLoading,
    connectGmail,
    connectMicrosoft,
    syncAccount,
    disconnectAccount,
  };
}
